import { useQueryClient } from '@tanstack/react-query';
import { useRouter, useRouterState } from '@tanstack/react-router';
import { ErrorBoundary } from 'common/components/errorBoundary';
import EscalationPrompt from 'common/components/escalationPrompt';
import PendingComponentLoader from 'common/components/pendingComponentLoader';
import { getShowNavBar, isMobileUserAgent, removeCookie } from 'common/utils/utils';
import { QUERIES_KEYS } from 'constant';
import { APP_CONSTANTS } from 'constants/app';
import { AUTH_CONSTANTS } from 'constants/auth';
import { FEATURE_FLAGS_KEYS } from 'constants/featureFlags';
import { clearStorage } from 'features/Logout/utils';
import NavigationBar from 'features/navBar';
import { COMPANY_FIELD_KEY, SESSION_STORAGE } from 'features/queue/constants';
import {
  selectActiveView,
  selectColumnsOrder,
  selectDefaultView,
  selectOriginalView,
  setActiveView,
  setColumnsOrder,
  setDefaultView,
  setOriginalView,
} from 'features/queue/slice';
import { getViewInitializedColumnConfig } from 'features/queue/utils';
import { BASIC_EDITION } from 'features/settings/constants';
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useApplicationData } from 'remote-state/applicationHooks';
import { useFeatureFlagQuery } from 'remote-state/featureFlagsHooks';
import { handleLocalHostUserDetails, isLocalHost } from 'services/localhost';
import { details, selectActiveUser } from 'store/userSlice';
import TrialBanner from 'common/components/trial-page/trial-banner';
import useIsTrialEnabled from 'common/components/trial-page/hooks/useIsTrialEnabled';
import DriftChat from '../../common/components/drift-chat/DriftChat';
import { useGeneralWebSocketDataSync } from '../../remote-state/web-sockets/useGeneralWebSocketDataSync';
import { getUserMobileLink } from '../../services/userService';
import ReloadPagePrompt from './reloadPagePrompt';
import { collapse, openChatbot, selectIsChatbotOpen, toggle } from './slice';
import { StyledContainer } from './style';
import usePendoInformation from './usePendoInformation';

const AdminChatbot = lazy(() => import('features/adminChatbot'));

const editions = new Map([
  [0, 'Basic Edition'],
  [2, 'Full Edition'],
]);

async function isMobileUserAgentRedirected(userAgent, userRole) {
  if (isMobileUserAgent(userAgent) && userRole === AUTH_CONSTANTS.ADMIN) {
    const res = await getUserMobileLink();
    if (res?.mobileAppServerUrl?.length > 0) {
      window.location.href = res.mobileAppServerUrl;
      return true;
    }
  }
  return false;
}

function getBrowserType() {
  const userAgent = navigator.userAgent;
  if (userAgent.indexOf('MSIE') !== -1 || navigator.appVersion.indexOf('Trident/') > 0) {
    return 'Internet Explorer';
  }
  if (userAgent.indexOf('Edge') !== -1) {
    return 'Microsoft Edge';
  }
  if (userAgent.indexOf('Firefox') !== -1) {
    return 'Mozilla Firefox';
  }
  if (userAgent.indexOf('Chrome') !== -1) {
    return 'Google Chrome';
  }
  if (userAgent.indexOf('Safari') !== -1) {
    return 'Apple Safari';
  }
  if (userAgent.indexOf('Opera') !== -1 || userAgent.indexOf('OPR') !== -1) {
    return 'Opera';
  }
  return 'Unknown browser';
}

const handleBeforeLoad = async ({ currentUser, dispatch, isAiEnabled, edition, defaultUi, getPendoInformation }) => {
  if (isLocalHost()) {
    if (currentUser?.isAuth) {
      if (window.permissionInitialized) return;
      window.permissionInitialized = true;
    } else {
      handleLocalHostUserDetails(dispatch);
    }
    return;
  }
  const isClassicRedirected = localStorage.getItem(APP_CONSTANTS.IS_CLASSIC_REDIRECTED);
  if (defaultUi === 'CLASSIC' && !isClassicRedirected) {
    removeCookie(APP_CONSTANTS.IN_SPACES_IFRAME_COOKIE);
    localStorage.setItem(APP_CONSTANTS.IS_CLASSIC_REDIRECTED, 'true');
    window.open(window.location.origin, '_self', 'noreferrer');
    return;
  }
  if (currentUser?.roles[0] === AUTH_CONSTANTS.END_USER) {
    window.location.href = '/servicePortal';
    return;
  }

  const isQAMode =
    localStorage.getItem(APP_CONSTANTS.IS_QA_MODE) || localStorage.getItem(APP_CONSTANTS.IS_QA_MODE) === null;

  if (currentUser?.isAuth) {
    const { account, visitor } = await getPendoInformation();

    window.pendo?.initialize({
      visitor: {
        id: `${localStorage.getItem('account_id')}_${currentUser?.userId}`,
        visitorId: `${localStorage.getItem('account_id')}_${currentUser?.userId}`,
        accountId: `${localStorage.getItem('account_id')}`,
        userLanguage: currentUser?.language,
        calculatedRole: currentUser?.calculatedRole,
        browserType: getBrowserType(),
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        deviceMemory: navigator?.deviceMemory,
        ...visitor,
      },
      account: {
        id: localStorage.getItem('account_id'),
        isSpaces: true,
        isQAMode,
        isOnDemand: true,
        edition: editions.get(edition) ?? 'Unknown',
        isAI: isAiEnabled,
        ...account,
      },
    });
    if (window.permissionInitialized) return;
    window.permissionInitialized = true;
  }

  isMobileUserAgentRedirected(navigator.userAgent, currentUser?.roles[0]);
};

const PrivatePagesComponentWrapper = ({ children }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector(selectActiveUser);
  const userDetails = useSelector(details);
  const { isFetched, isFetching } = useApplicationData(QUERIES_KEYS.APPLICATION_DATA, { forceCall: true });
  const { data: isAiEnabled } = useApplicationData(QUERIES_KEYS.IS_AI_ENABLED);
  const { data: edition } = useApplicationData(QUERIES_KEYS.EDITION);
  const { data: defaultUi } = useApplicationData(QUERIES_KEYS.DEFAULT_UI);
  const showContent = isLocalHost() ? isFetched : isFetched && !isFetching;
  const getPendoInformation = usePendoInformation();

  useEffect(() => {
    handleBeforeLoad({ currentUser, userDetails, dispatch, isAiEnabled, edition, defaultUi, getPendoInformation });
  }, [currentUser, userDetails, dispatch, isAiEnabled, edition, defaultUi, getPendoInformation]);

  return showContent ? <>{children}</> : <PendingComponentLoader />;
};

export default function LoggedInRoutesPanel({ children }) {
  const dispatch = useDispatch();
  const { isReloadPromptOpen, setReloadPromptOpen } = useGeneralWebSocketDataSync();
  useApplicationData(QUERIES_KEYS.APPLICATION_DATA);
  const router = useRouter();
  const routerSearch = useRouterState({
    select: (state) => state.location.searchStr,
  });

  const activeView = useSelector(selectActiveView);
  const defaultView = useSelector(selectDefaultView);
  const originalView = useSelector(selectOriginalView);
  const columnsOrder = useSelector(selectColumnsOrder);
  const isChatbotOpen = useSelector(selectIsChatbotOpen);
  const queryClient = useQueryClient();
  const [menuExpanded, setMenuExpanded] = useState(
    JSON.parse(localStorage.getItem(APP_CONSTANTS.IS_MENU_EXPANDED)) === true,
  );
  const { data: edition } = useApplicationData(QUERIES_KEYS.EDITION);
  const isBasicEdition = edition === BASIC_EDITION;
  const userDetails = useSelector(details);
  const userDetailsRef = useRef(userDetails);
  const isTrialEnabled = useIsTrialEnabled();

  useEffect(() => {
    userDetailsRef.current = userDetails;
  }, [userDetails]);

  const { data: isDriftChatEnabled } = useFeatureFlagQuery({
    flagKey: FEATURE_FLAGS_KEYS.DRIFT_CHAT_ENABLED,
    defaultValue: false,
  });

  const showNavBar = getShowNavBar(router);

  const toggleNavBarCollapse = useCallback(() => {
    localStorage.setItem(APP_CONSTANTS.IS_MENU_EXPANDED, !menuExpanded);
    setMenuExpanded(!menuExpanded);
    setTimeout(() => dispatch(toggle()));
  }, [dispatch, menuExpanded]);

  const clearStorageByEdition = useCallback(() => {
    if (columnsOrder) {
      dispatch(setColumnsOrder(columnsOrder.filter((column) => column.fieldName !== COMPANY_FIELD_KEY)));
    }

    let columnsConfig;

    if (defaultView?.columnsConfig) {
      columnsConfig = getViewInitializedColumnConfig(defaultView.columnsConfig);
      dispatch(
        setDefaultView((prev) => ({
          ...prev,
          columnsConfig,
        })),
      );
    }

    if (originalView?.columnsConfig) {
      columnsConfig = getViewInitializedColumnConfig(originalView.columnsConfig);

      dispatch(
        setOriginalView((prev) => ({
          ...prev,
          columnsConfig,
        })),
      );
    }

    if (activeView?.columnsConfig) {
      columnsConfig = getViewInitializedColumnConfig(activeView.columnsConfig);

      dispatch(
        setActiveView((prev) => ({
          ...prev,
          columnsConfig,
        })),
      );
    }
  }, [columnsOrder, activeView, defaultView, originalView, dispatch]);

  useEffect(() => {
    if (!edition) return;

    const storedEdition = sessionStorage.getItem(SESSION_STORAGE.edition);
    if (isBasicEdition && (storedEdition === null || Number(edition) !== Number(storedEdition))) {
      clearStorageByEdition();
    }
    if (!storedEdition || Number(edition) !== Number(storedEdition)) {
      sessionStorage.setItem(SESSION_STORAGE.edition, edition);
    }
  }, [edition, isBasicEdition, clearStorageByEdition]);

  const handleReloadPromptClick = () => {
    setReloadPromptOpen(false);
    clearStorage(true, true);
    queryClient.resetQueries();
    setTimeout(() => {
      window.location.reload(true);
    }, 100);
  };

  const AdminChatbotFallback = () => (
    <div style={{ width: '30%' }}>
      <PendingComponentLoader />
    </div>
  );

  useEffect(
    () => () => {
      // Clean up Pendo on component unmount
      if (window.pendo) {
        window.pendo.clearGuideMemory?.();
        window.pendo.clearResourceCenter?.();
      }
    },
    [],
  );

  const handleAIBuilderOpened = useCallback(() => {
    const isAIBuilderOpened = routerSearch.includes('ai-builder');

    if (isAIBuilderOpened) {
      dispatch(openChatbot(false));
      localStorage.setItem(APP_CONSTANTS.IS_MENU_EXPANDED, false);
      setMenuExpanded(false);
      setTimeout(() => dispatch(collapse()), 0);
    }
  }, [dispatch, routerSearch]);

  useEffect(() => {
    handleAIBuilderOpened();
  }, [handleAIBuilderOpened]);

  return (
    <PrivatePagesComponentWrapper>
      {isTrialEnabled && <TrialBanner />}
      <ReloadPagePrompt onClick={handleReloadPromptClick} open={isReloadPromptOpen} />
      <ErrorBoundary>
        {showNavBar ? (
          <>
            <NavigationBar toggleNavBarCollapse={toggleNavBarCollapse} navBarCollapsed={!menuExpanded} />
            <StyledContainer className={isChatbotOpen ? 'chatbot-open' : ''}>
              {children}
              {isDriftChatEnabled && <DriftChat />}
              {isChatbotOpen && (
                <Suspense fallback={<AdminChatbotFallback />}>
                  <AdminChatbot />
                </Suspense>
              )}
            </StyledContainer>
          </>
        ) : (
          { children }
        )}
        <EscalationPrompt />
      </ErrorBoundary>
    </PrivatePagesComponentWrapper>
  );
}
